import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { InternalAccessUser } from 'src/interfaces/internalAccess'
import { getInternalUsers, getInternalUsersError } from 'src/selectors/internalAccess'
import { filterInternalUsers } from 'src/utils/internalAccess/internalUsers'
import { InternalUserModal } from './InternalUserModal'
import { InternalUsersTable } from './InternalUsersTable'
import { InternalUsersToolbar } from './InternalUsersToolbar'
import { InviteInternalUserModal } from './InviteInternalUserModal'

interface Props {
  onRefresh(): void
}

/**
 * No fetch here: the users count is a tab subtitle and the Roles tab counts users per role, so
 * `UsersManagementPage` loads the list whichever tab is open.
 */
const InternalUsersTabComponent = ({ onRefresh }: Props) => {
  const users = useSelector(getInternalUsers)
  const usersError = useSelector(getInternalUsersError)

  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [isInviteOpen, setIsInviteOpen] = useState<boolean>(false)

  const filteredUsers = useMemo(() => {
    return filterInternalUsers(users, searchQuery)
  }, [users, searchQuery])

  const handleRowClick = useCallback((user: InternalAccessUser) => {
    setSelectedUserId(user.id)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedUserId(null)
  }, [])

  const handleInviteClick = useCallback(() => {
    setIsInviteOpen(true)
  }, [])

  const handleCloseInvite = useCallback(() => {
    setIsInviteOpen(false)
  }, [])

  return (
    <Fragment>
      <InternalUsersToolbar
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        onRefresh={onRefresh}
        onInviteClick={handleInviteClick}
      />
      {usersError ? (
        <SectionCard title="Users">
          <Typography variant="body2" css={tw`text-critical-500`}>
            {usersError}
          </Typography>
        </SectionCard>
      ) : null}
      <InternalUsersTable users={filteredUsers} onRowClick={handleRowClick} />
      <InternalUserModal userId={selectedUserId} onClose={handleCloseModal} />
      <InviteInternalUserModal isOpen={isInviteOpen} onClose={handleCloseInvite} />
    </Fragment>
  )
}

export const InternalUsersTab = memo(InternalUsersTabComponent)
